import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useTranslation } from "react-i18next"
import { ArrowLeft } from "lucide-react"
import { useProduct, useCreateProduct, useUpdateProduct, useProductCategories } from "@/hooks/use-products"
import { ApiClientError } from "@/lib/api-client"
import { toast } from "sonner"

const productSchema = z.object({
  name: z.string().min(1, "Nazwa jest wymagana"),
  sku: z.string().optional(),
  unit: z.string().min(1, "Jednostka jest wymagana"),
  net_price: z.string().optional(),
  vat_rate: z.string().optional(),
  category_id: z.string().optional(),
  description: z.string().optional(),
  is_active: z.boolean().optional(),
})

type ProductForm = z.infer<typeof productSchema>

export function ProductFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id
  const [error, setError] = useState<string | null>(null)
  const [grossPreview, setGrossPreview] = useState<number | null>(null)

  const { data: productData } = useProduct(id)
  const { data: categoriesData } = useProductCategories()
  const createMutation = useCreateProduct()
  const updateMutation = useUpdateProduct()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ProductForm>({
    resolver: zodResolver(productSchema),
    values: isEdit && productData ? {
      name: productData.data.name,
      sku: productData.data.sku || "",
      unit: productData.data.unit,
      net_price: productData.data.net_price != null ? String(productData.data.net_price) : "",
      vat_rate: productData.data.vat_rate != null ? String(productData.data.vat_rate) : "",
      category_id: productData.data.category?.id || "",
      description: productData.data.description || "",
      is_active: productData.data.is_active,
    } : {
      name: "",
      sku: "",
      unit: "szt.",
      net_price: "",
      vat_rate: "23",
      category_id: "",
      description: "",
      is_active: true,
    },
  })

  const netPrice = watch("net_price")
  const vatRate = watch("vat_rate")

  useEffect(() => {
    const net = parseFloat(netPrice || "")
    const vat = parseFloat(vatRate || "")
    if (!isNaN(net) && !isNaN(vat) && net > 0) {
      setGrossPreview(Math.round(net * (1 + vat / 100) * 100) / 100)
    } else {
      setGrossPreview(null)
    }
  }, [netPrice, vatRate])

  const onSubmit = async (data: ProductForm) => {
    try {
      setError(null)
      const payload = {
        ...data,
        net_price: data.net_price ? parseFloat(data.net_price) : null,
        vat_rate: data.vat_rate ? parseFloat(data.vat_rate) : null,
        category_id: data.category_id || null,
      }
      if (isEdit) {
        await updateMutation.mutateAsync({ id, data: payload })
        toast.success("Produkt zaktualizowany")
      } else {
        await createMutation.mutateAsync(payload)
        toast.success("Produkt utworzony")
      }
      navigate("/products")
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message)
      else setError("Wystąpił nieoczekiwany błąd")
    }
  }

  const categories = categoriesData?.data ?? []

  const flattenCategories = (cats: typeof categories, depth = 0): Array<{ id: string; name: string; depth: number }> => {
    const result: Array<{ id: string; name: string; depth: number }> = []
    for (const cat of cats) {
      result.push({ id: cat.id, name: cat.name, depth })
      if (cat.children?.length) {
        result.push(...flattenCategories(cat.children, depth + 1))
      }
    }
    return result
  }

  const flatCategories = flattenCategories(categories)
  const inputClass = "h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm text-foreground outline-none"

  return (
    <div className="mx-auto max-w-[720px] space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/50">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-foreground">
          {isEdit ? "Edytuj produkt" : t("products.new")}
        </h1>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Dane produktu</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Nazwa *</label>
              <input {...register("name")} className={`${inputClass} ${errors.name ? "border-destructive" : ""}`} />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">SKU</label>
              <input {...register("sku")} className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Jednostka *</label>
              <input {...register("unit")} className={inputClass} />
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Cennik</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Cena netto (PLN)</label>
              <input {...register("net_price")} type="number" step="0.01" min="0" className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Stawka VAT (%)</label>
              <input {...register("vat_rate")} type="number" step="0.01" min="0" max="100" className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Cena brutto</label>
              <div className="flex h-9 items-center rounded-md border border-input bg-muted px-3 text-sm font-mono text-muted-foreground">
                {grossPreview != null ? `${grossPreview.toFixed(2)} zł` : "—"}
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Dodatkowe</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Kategoria</label>
              <select {...register("category_id")} className={inputClass}>
                <option value="">— Brak —</option>
                {flatCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {"  ".repeat(cat.depth)}{cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm pt-6 text-muted-foreground">
                <input type="checkbox" {...register("is_active")} className="rounded" />
                Aktywny
              </label>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">Opis</label>
            <textarea {...register("description")} rows={3}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none" />
          </div>
        </section>

        <div className="flex justify-end gap-3 border-t border-border pt-4">
          <button type="button" onClick={() => navigate(-1)}
            className="h-9 rounded-md border border-input px-4 text-sm font-medium text-foreground">
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={isSubmitting}
            className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50">
            {isSubmitting ? t("common.loading") : t("common.save")}
          </button>
        </div>
      </form>
    </div>
  )
}
